import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Star, Clock, ShoppingCart, X, Plus, Minus, Play, Pause, Sparkles } from 'lucide-react';

const API = `${window.location.origin}/api`;

export default function PerfumeCatalog() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cart, setCart] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [activeCollection, setActiveCollection] = useState('all');

    useEffect(() => {
        axios.get(`${API}/catalog/${slug}`).then(res => setData(res.data)).catch(() => {}).finally(() => setLoading(false));
    }, [slug]);

    const addToCart = (perfume) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === perfume.id);
            if (existing) return prev.map(i => i.id === perfume.id ? { ...i, qty: i.qty + 1 } : i);
            return [...prev, { id: perfume.id, name: perfume.name, brand: perfume.brand, price: perfume.price, image_url: perfume.image_url, qty: 1 }];
        });
    };

    const updateQty = (id, delta) => {
        setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(0, i.qty + delta) } : i).filter(i => i.qty > 0));
    };

    const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
    const cartCount = cart.reduce((s, i) => s + i.qty, 0);

    if (loading) return (
        <div className="min-h-screen bg-[#080810] flex items-center justify-center">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
    );
    if (!data) return <div className="min-h-screen bg-[#080810] flex items-center justify-center text-white/30 text-sm">Shop not found</div>;

    const { shop, perfumes } = data;
    const primary = shop.primary_color || '#D4AF37';
    const bg = shop.background_color || '#080810';
    const font = shop.font_family || 'Cormorant Garamond';

    // Get unique collections from perfumes
    const allCollections = [...new Set(perfumes.flatMap(p => p.collections || []))];
    const filtered = activeCollection === 'all' ? perfumes : perfumes.filter(p => (p.collections || []).includes(activeCollection));

    return (
        <div className="min-h-screen pb-28" style={{ backgroundColor: bg, fontFamily: `'${font}', serif` }} data-testid="perfume-catalog">
            <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${encodeURIComponent(font)}:wght@300;400;500;600;700&display=swap`} />

            {/* Hero Header */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="relative overflow-hidden"
            >
                <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at top, ${primary}08 0%, transparent 70%)` }} />
                <div className="relative text-center pt-14 pb-10 px-5">
                    {shop.logo_url ? (
                        <motion.img initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }} src={shop.logo_url} alt="" className="w-20 h-20 object-contain mx-auto mb-4 rounded-2xl" />
                    ) : (
                        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.2 }} className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center border border-white/[0.06]" style={{ backgroundColor: `${primary}15` }}>
                            <Sparkles className="w-10 h-10" style={{ color: primary }} />
                        </motion.div>
                    )}
                    <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }} className="text-3xl font-light tracking-[0.15em] uppercase" style={{ color: primary }}>
                        {shop.name}
                    </motion.h1>
                    {shop.description && (
                        <motion.p initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="text-white/25 text-sm mt-3 max-w-sm mx-auto leading-relaxed">
                            {shop.description}
                        </motion.p>
                    )}
                    {/* Decorative line */}
                    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.5, duration: 0.8 }} className="w-16 h-px mx-auto mt-6" style={{ backgroundColor: primary }} />
                </div>
            </motion.div>

            {/* Collection Filter Tabs */}
            {allCollections.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="px-4 mb-8 overflow-x-auto scrollbar-hide">
                    <div className="flex gap-2 justify-center flex-wrap">
                        <button onClick={() => setActiveCollection('all')} className={`px-4 py-2 rounded-full text-xs tracking-wider uppercase transition-all ${activeCollection === 'all' ? 'text-white' : 'text-white/25 hover:text-white/40'}`} style={activeCollection === 'all' ? { backgroundColor: `${primary}30`, color: primary, border: `1px solid ${primary}40` } : { border: '1px solid rgba(255,255,255,0.06)' }}>
                            All
                        </button>
                        {allCollections.map(c => (
                            <button key={c} onClick={() => setActiveCollection(c)} className={`px-4 py-2 rounded-full text-xs tracking-wider uppercase transition-all ${activeCollection === c ? 'text-white' : 'text-white/25 hover:text-white/40'}`} style={activeCollection === c ? { backgroundColor: `${primary}30`, color: primary, border: `1px solid ${primary}40` } : { border: '1px solid rgba(255,255,255,0.06)' }}>
                                {c.replace(/_/g, ' ')}
                            </button>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Perfume Grid */}
            <div className="max-w-lg mx-auto px-4 space-y-5">
                <AnimatePresence mode="wait">
                    {filtered.map((p, i) => (
                        <PerfumeCard key={p.id} perfume={p} index={i} slug={slug} primary={primary} onAddToCart={() => addToCart(p)} />
                    ))}
                </AnimatePresence>
                {filtered.length === 0 && (
                    <div className="text-center py-20 text-white/15">
                        <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No perfumes in this collection</p>
                    </div>
                )}
            </div>

            {/* Floating Cart */}
            <AnimatePresence>
                {cartCount > 0 && (
                    <motion.button
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        onClick={() => setShowCart(true)}
                        className="fixed bottom-6 right-6 h-14 px-6 rounded-full shadow-2xl flex items-center gap-3 z-50"
                        style={{ backgroundColor: primary, boxShadow: `0 8px 32px ${primary}40` }}
                        data-testid="floating-cart-btn"
                    >
                        <ShoppingCart className="w-5 h-5 text-white" />
                        <span className="text-white font-semibold">{cartCount}</span>
                        <span className="text-white/60 text-sm">|</span>
                        <span className="text-white text-sm font-medium">QAR {cartTotal.toFixed(0)}</span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Cart Drawer */}
            <AnimatePresence>
                {showCart && (
                    <div className="fixed inset-0 z-[100]" data-testid="cart-drawer">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCart(false)} />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                            className="absolute bottom-0 left-0 right-0 max-h-[85vh] rounded-t-3xl overflow-y-auto"
                            style={{ backgroundColor: bg, borderTop: `1px solid ${primary}20` }}
                        >
                            <div className="p-6">
                                <div className="w-10 h-1 rounded-full bg-white/10 mx-auto mb-6" />
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-light tracking-wider uppercase" style={{ color: primary }}>Your Selection</h2>
                                    <button onClick={() => setShowCart(false)} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"><X className="w-4 h-4 text-white/40" /></button>
                                </div>
                                <div className="space-y-3 mb-6">
                                    {cart.map(item => (
                                        <div key={item.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.04]">
                                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/[0.03] flex-shrink-0">
                                                {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Sparkles className="w-5 h-5 text-white/10" /></div>}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white font-medium truncate">{item.name}</p>
                                                <p className="text-[10px] text-white/25 uppercase tracking-wider">{item.brand}</p>
                                            </div>
                                            <div className="flex items-center gap-2 bg-white/[0.03] rounded-full px-1 py-1">
                                                <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"><Minus className="w-3 h-3 text-white/40" /></button>
                                                <span className="text-sm text-white w-5 text-center font-medium">{item.qty}</span>
                                                <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"><Plus className="w-3 h-3 text-white/40" /></button>
                                            </div>
                                            <p className="text-sm font-semibold min-w-[70px] text-right" style={{ color: primary }}>QAR {(item.price * item.qty).toFixed(0)}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex items-center justify-between py-5 border-t border-white/[0.04] mb-5">
                                    <span className="text-white/30 uppercase text-xs tracking-wider">Total</span>
                                    <span className="text-3xl font-light" style={{ color: primary }}>QAR {cartTotal.toFixed(0)}</span>
                                </div>
                                <Link to={`/catalog/${slug}/checkout`} state={{ cart, shop }} onClick={() => setShowCart(false)}>
                                    <button className="w-full py-4 rounded-2xl text-white font-medium tracking-wider uppercase text-sm transition-all hover:brightness-110" style={{ backgroundColor: primary }} data-testid="checkout-btn">
                                        Proceed to Checkout
                                    </button>
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

/* Individual Perfume Card with video support */
function PerfumeCard({ perfume: p, index, slug, primary, onAddToCart }) {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(true);
    const hasVideo = !!p.video_url;

    // Force autoplay on mount
    useEffect(() => {
        if (videoRef.current && hasVideo) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(() => {});
        }
    }, [hasVideo]);

    const toggleVideo = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (videoRef.current) {
            if (playing) { videoRef.current.pause(); setPlaying(false); }
            else { videoRef.current.play().catch(() => {}); setPlaying(true); }
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            <Link to={`/catalog/${slug}/perfume/${p.id}`} className="block group">
                <div className="rounded-3xl overflow-hidden border border-white/[0.04] bg-white/[0.02] backdrop-blur-sm transition-all group-hover:border-white/[0.08] group-hover:bg-white/[0.03]">
                    {/* Media Section - Full width image/video */}
                    {(p.image_url || hasVideo) && (
                        <div className="relative aspect-[4/3] overflow-hidden bg-black/20">
                            {hasVideo ? (
                                <>
                                    <video
                                        ref={videoRef}
                                        src={p.video_url}
                                        poster={p.image_url}
                                        className="w-full h-full object-cover"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        webkit-playsinline=""
                                        preload="auto"
                                    />
                                    <button
                                        onClick={toggleVideo}
                                        className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/10 transition-all hover:bg-black/70"
                                    >
                                        {playing ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white ml-0.5" />}
                                    </button>
                                </>
                            ) : (
                                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            )}
                            {/* Gradient overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            {/* Price tag */}
                            <div className="absolute bottom-3 left-4">
                                <span className="text-2xl font-light text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>QAR {p.price}</span>
                            </div>
                        </div>
                    )}

                    {/* Info */}
                    <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-1">{p.brand}</p>
                                <p className="text-xl font-medium text-white leading-tight">{p.name}</p>
                            </div>
                            {p.rating > 0 && (
                                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.06]">
                                    <Star className="w-3 h-3 fill-current" style={{ color: primary }} />
                                    <span className="text-xs font-medium" style={{ color: primary }}>{p.rating}</span>
                                </div>
                            )}
                        </div>

                        {/* Accords as colored dots */}
                        {p.main_accords?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {p.main_accords.slice(0, 5).map((a, i) => (
                                    <span key={i} className="px-2.5 py-1 rounded-full text-[9px] font-medium text-white/80 tracking-wide" style={{ backgroundColor: `${a.color}30`, border: `1px solid ${a.color}40` }}>{a.name}</span>
                                ))}
                            </div>
                        )}

                        {/* Notes preview */}
                        {p.top_notes && (
                            <p className="text-[11px] text-white/20 mt-3 line-clamp-1">
                                {p.top_notes}
                            </p>
                        )}

                        {/* Quick info row */}
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/[0.04]">
                            <div className="flex items-center gap-3">
                                {p.longevity && <span className="flex items-center gap-1 text-[10px] text-white/20"><Clock className="w-3 h-3" />{p.longevity.replace('_', ' ')}</span>}
                                {p.gender && p.gender !== 'unisex' && <span className="text-[10px] text-white/15 uppercase">{p.gender}</span>}
                                {!p.image_url && !hasVideo && <span className="text-lg font-light" style={{ color: primary }}>QAR {p.price}</span>}
                            </div>
                            <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAddToCart(); }}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90 hover:brightness-110"
                                style={{ backgroundColor: primary, boxShadow: `0 4px 16px ${primary}30` }}
                                data-testid={`add-to-cart-${p.id}`}
                            >
                                <Plus className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
